export const getChainIdFromAddressFormat = (addressFormat: string) => {
    switch (addressFormat) {
        case 'ADDRESS_FORMAT_ETHEREUM':
            return 'evm:eip155:1';
        case 'ADDRESS_FORMAT_SOLANA':
            return 'solana:mainnet-beta';
		default:
			return 'evm:eip155:1';
    }
}

export type ChainId = 'evm' | 'solana';

export interface TurnkeyConfig {
    organizationId: string;
    apiPublicKey: string;
    apiPrivateKey: string;
    baseUrl: string;
}

export interface Timestamp {
    seconds: string;
    nanos: string;
}

export interface Vote {
    id: string;
    userId: string;
    user: {
        userId: string;
        userName: string;
        userEmail: string;
        authenticators: {
            transports: string[]; // e.g., ["AUTHENTICATOR_TRANSPORT_INTERNAL"]
            attestationType: string; // e.g., "packed"
            aaguid: string;
            credentialId: string;
            model: string; // e.g., "Security key"
            credential: {
                publicKey: string;
                type: string; // e.g., "CREDENTIAL_TYPE_WEBAUTHN_AUTHENTICATOR"
            };
            authenticatorId: string;
            authenticatorName: string;
            createdAt: Timestamp;
            updatedAt: Timestamp;
        }[];
        apiKeys: {
            credential: {
                publicKey: string;
                type: string; // e.g., "CREDENTIAL_TYPE_API_KEY_P256"
            };
            apiKeyId: string;
            apiKeyName: string;
            createdAt: Timestamp;
            updatedAt: Timestamp;
        }[];
        userTags: string[];
        oauthProviders: {
            providerName: string;
            oidcToken: string;
        }[];
        createdAt: Timestamp;
        updatedAt: Timestamp;
    };
    activityId: string;
    selection: string; // e.g., "VOTE_SELECTION_APPROVED"
    message: string;
    publicKey: string;
    signature: string;
    scheme: string; // e.g., "SIGNATURE_SCHEME_TK_API_P256"
    createdAt: Timestamp;
}

export interface ListWalletsRequest {
    organizationId: string;
}

export interface ListWalletsResponse {
    activity: {
        id: string;
        status: string;
        type: string;
        organizationId: string;
        timestampMs: string;
        result: {
            wallets: {
                walletId: string;
                walletName: string;
                createdAt: Timestamp;
                updatedAt: Timestamp;
                exported: boolean;
                imported: boolean;
            }[];
        };
        votes: Vote[];
        fingerprint: string;
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any;
    };
}

export interface ListWalletAccountsRequest {
    organizationId: string;
    walletId: string;
    paginationOptions: {
        limit: string;
        before: string;
        after: string;
    };
}

export interface ListWalletAccountsResponse {
	accounts: {
		walletAccountId: string;
		organizationId: string;
		walletId: string;
		curve: string;
		pathFormat: string;
		path: string;
		addressFormat: string;
		address: string;
		createdAt: {
			seconds: string;
			nanos: string;
		},
		updatedAt: {
			seconds: string;
			nanos: string;
		},
		publicKey: string;
	}[]
}

export interface ApiKey {
    apiKeyName: string;
    publicKey: string;
    curveType: string;
}

export interface Authenticator {
    authenticatorName: string;
    challenge: string;
	attestation: any
}

export interface OauthProvider {
    oauthProviderName: string;
    oidcToken: string;
}

export interface RootUser {
    userName: string;
    apiKeys: ApiKey[];
    authenticators: Authenticator[];
    oauthProviders: OauthProvider[];
}

export interface Account {
    pathFormat: string;
    path: string;
    curve: string;
    addressFormat: string;
}

export interface Wallet {
    walletName: string;
    accounts: Account[];
    mnemonicLength: number;
}

export interface CreateWalletRequest {
    subOrganizationName: string;
    rootUsers: RootUser[];
    rootQuorumThreshold: number;
    wallet: Wallet;
}

export interface Timestamp {
    seconds: string;
    nanos: string;
}

export interface CreateWalletResponse {
    activity: {
        id: string;
        organizationId: string;
        status: string; // e.g., "ACTIVITY_STATUS_COMPLETED"
        type: "ACTIVITY_TYPE_CREATE_SUB_ORGANIZATION_V7";
        intent: {
            createSubOrganizationIntentV7: {
                subOrganizationName: string;
                rootUsers: {
                    userName: string;
                    apiKeys: {
                        apiKeyName: string;
                        publicKey: string;
                        curveType: string; // e.g., "API_KEY_CURVE_P256"
                    }[];
                    authenticators: {
                        transports: string[]; // e.g., ["AUTHENTICATOR_TRANSPORT_INTERNAL"]
                        attestationType: string; // e.g., "packed"
                        aaguid: string;
                        credentialId: string;
                        model: string; // e.g., "Security key"
                        credential: {
                            publicKey: string;
                            type: string; // e.g., "CREDENTIAL_TYPE_WEBAUTHN_AUTHENTICATOR"
                        };
                        authenticatorId: string;
                        authenticatorName: string;
                        createdAt: Timestamp;
                        updatedAt: Timestamp;
                    }[];
                    oauthProviders: {
                        providerName: string;
                        oidcToken: string;
                    }[];
                }[];
                rootQuorumThreshold: number;
                wallet: {
                    walletName: string;
                    accounts: {
                        curve: string; // e.g., "CURVE_SECP256K1"
                        pathFormat: string; // e.g., "PATH_FORMAT_BIP32"
                        path: string; // e.g., "m/44'/60'/0'/0"
                        addressFormat: string; // e.g., "ADDRESS_FORMAT_ETHEREUM"
                    }[];
                    mnemonicLength: number;
                };
            };
        };
        result: {
            createSubOrganizationResultV7: {
                subOrganizationId: string;
                wallet: {
                    walletId: string;
                    addresses: string[];
                };
                rootUserIds: string[];
            };
        };
        votes: Vote[];
        fingerprint: string; // e.g., "sha256:34dee4c7ce674052af1bb6dd4c47aebed7c0ccc7f123c142b1191afc330be3a5"
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any; // Can be null or an error object
    };
}

export interface AddWalletAccountsRequest {
    type: "ACTIVITY_TYPE_CREATE_WALLET_ACCOUNTS";
    timestampMs: string; // e.g., "1746736509954"
    organizationId: string;
    parameters: {
        walletId: string;
        accounts: {
            curve: string;
            pathFormat: string;
            path: string;
            addressFormat: string;
        }[];
    };
}

export interface AddWalletAccountsResponse {
    activity: {
        id: string;
        status: string;
        type: "ACTIVITY_TYPE_CREATE_WALLET_ACCOUNTS";
        organizationId: string;
        timestampMs: string;
        intent: {
            createWalletAccountsIntent: {
                walletId: string;
                accounts: {
                    curve: string;
                    pathFormat: string;
                    path: string;
                    addressFormat: string;
                }[];
            };
        };
        result: {
            createWalletAccountsResult: {
                addresses: string[];
            };
        };
        votes: Vote[];
        fingerprint: string;
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any;
    };
}

export interface SignRawPayloadRequest {
    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2";
    timestampMs: string; // e.g., "1746736509954"
    organizationId: string;
    parameters: {
        signWith: string; // Likely a privateKeyId or wallet address
        payload: string;
        encoding: string;
        hashFunction: string;
    };
}

export interface SignRawPayloadResponse {
    activity: {
        id: string;
        status: string;
        type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOAD_V2";
        organizationId: string;
        timestampMs: string;
        intent: {
            signRawPayloadIntent: {
                privateKeyId: string;
                payload: string;
                encoding: string;
                hashFunction: string;
            };
        };
        result: {
            signRawPayloadResult: {
                r: string;
                s: string;
                v: string;
            };
        };
        votes: Vote[];
        fingerprint: string;
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any;
    };
}

export interface SignRawPayloadsRequest {
    type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOADS";
    timestampMs: string; // e.g., "1746736509954"
    organizationId: string;
    parameters: {
        signWith: string; // Likely a privateKeyId or wallet address
        payloads: string[];
        encoding: string;
        hashFunction: string;
    };
}

export interface SignRawPayloadsResponse {
    activity: {
        id: string;
        status: string;
        type: "ACTIVITY_TYPE_SIGN_RAW_PAYLOADS";
        organizationId: string;
        timestampMs: string;
        intent: {
            signRawPayloadsIntent: {
                signWith: string;
                payloads: string[];
                encoding: string;
                hashFunction: string;
            };
        };
        result: {
            signRawPayloadsResult: {
                signatures: {
                    r: string;
                    s: string;
                    v: string;
                }[];
            };
        };
        votes: Vote[];
        fingerprint: string;
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any;
    };
}

export interface SignTransactionRequest {
    type: "ACTIVITY_TYPE_SIGN_TRANSACTION_V2";
    timestampMs: string; // e.g., "1746736509954"
    organizationId: string;
    parameters: {
        signWith: string; // Likely a privateKeyId or wallet address
        unsignedTransaction: string; // Hex-encoded unsigned transaction
        type: string;
    };
}

export interface SignTransactionResponse {
    activity: {
        id: string;
        status: string;
        type: "ACTIVITY_TYPE_SIGN_TRANSACTION_V2";
        organizationId: string;
        timestampMs: string;
        intent: {
            signTransactionIntent: {
                privateKeyId: string;
                unsignedTransaction: string;
                type: string;
            };
        };
        result: {
            signTransactionResult: {
                signedTransaction: string;
            };
        };
        votes: Vote[];
        fingerprint: string;
        canApprove: boolean;
        canReject: boolean;
        createdAt: Timestamp;
        updatedAt: Timestamp;
        failure: null | any;
    };
}

export interface ABISpec {
    url?: string;
    abi?: any[];
    cacheTTL?: number;
}

export interface IDLSpec {
    url?: string;
    idl?: any;
    cacheTTL?: number;
}

interface ActionData {
    method: 'signMessage' | 'signTypedData' | 'signTransaction' | 'signRawPayloads';
    chain?: string;
    walletAddress: string;
    organizationId: string;
    contractAddress?: string;
    programId?: string;
    payload: any;
    abi?: ABISpec;
    idl?: IDLSpec;
    metadata?: {
        description?: string;
        riskLevel?: 'low' | 'medium' | 'high';
        estimatedGas?: string;
        estimatedValue?: string;
    };
}

export interface TurnkeyConfig {
    organizationId: string;
    apiPublicKey: string;
    apiPrivateKey: string;
    baseUrl: string;
}

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    decodedContent?: {
        evmFunction?: {
            functionName: string;
            functionSignature: string;
            args: Record<string, any>;
        };
        solanaInstructions?: Array<{
            instruction: string;
            programName?: string;
            data: Record<string, any>;
        }>;
    };
    riskAssessment?: {
        level: 'low' | 'medium' | 'high';
        factors: string[];
        recommendations?: string[];
    };
}
